import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Settings,
    Upload,
    Save,
    Eye,
    EyeOff,
    Check,
    X,
    Clock,
    Phone,
    Mail,
    MapPin,
    Instagram,
    Facebook,
    Package,
    Search,
    FileText,
    Plus,
    Trash2,
    UtensilsCrossed,
    ImageIcon,
    Pencil,
    Calendar,
    PartyPopper,
    Users,
    ExternalLink,
    Salad,
    Soup,
    Cake,
    GlassWater,
    Coffee,
    Wine,
    Sun,
    BookOpen,
    Scale
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

// ============================================================
// ADMIN - GESTION DU SITE CLIENT
// Structure simplifiée : Carte, Réservations, Paramètres, Descriptions, Disponibilité
// ============================================================

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Catégories de la carte dans l'ordre exact
const MENU_CATEGORIES = [
    { id: 'petit_dejeuner', name: 'Petit-déjeuner', icon: Sun },
    { id: 'entrees', name: 'Entrées', icon: Salad },
    { id: 'plats', name: 'Plats', icon: Soup },
    { id: 'desserts', name: 'Desserts', icon: Cake },
    { id: 'boissons_fraiches', name: 'Boissons fraîches', icon: GlassWater },
    { id: 'boissons_chaudes', name: 'Boissons chaudes', icon: Coffee },
    { id: 'cocktails', name: 'Cocktails', icon: Wine },
    { id: 'formules', name: 'Formules', icon: UtensilsCrossed },
];

// Tab Button
const TabButton = ({ active, onClick, icon: Icon, label, count }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
            active
                ? 'bg-amber-500 text-black'
                : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
        }`}
    >
        <Icon className="w-4 h-4" />
        <span>{label}</span>
        {count !== undefined && (
            <span className={`px-2 py-0.5 rounded-full text-xs ${active ? 'bg-black/20' : 'bg-neutral-600'}`}>
                {count}
            </span>
        )}
    </button>
);

// ============================================================
// ONGLET CARTE - Gestion du catalogue SITE CLIENT (séparé du POS)
// ============================================================
const CarteTab = ({ onRefresh }) => {
    const { api } = useAuth();
    const [activeCategory, setActiveCategory] = useState('petit_dejeuner');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingItem, setEditingItem] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef(null);
    const csvInputRef = useRef(null);
    
    // State for bulk import
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [bulkImportData, setBulkImportData] = useState('');
    const [bulkImporting, setBulkImporting] = useState(false);
    
    // Form state - UNIQUEMENT les champs du site client
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        category_id: 'petit_dejeuner',
        image_url: '',
        sort_order: 999,
        visible: true,
        is_available: true,
        is_popular: false,
        is_new: false
    });
    
    // Charger les produits du catalogue site client
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await api().get('/client-menu/admin/products');
            setProducts(response.data || []);
        } catch (error) {
            console.error('Error fetching client menu products:', error);
            toast.error('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchProducts();
    }, [api]);
    
    // Filtrer par catégorie
    const getCategoryItems = () => {
        return products.filter(p => p.category_id === activeCategory && !p.is_deleted);
    };
    
    const items = getCategoryItems();
    const categoryInfo = MENU_CATEGORIES.find(c => c.id === activeCategory);
    
    // Compter les produits par catégorie
    const getCategoryCount = (catId) => {
        return products.filter(p => p.category_id === catId && !p.is_deleted).length;
    };
    
    // Ouvrir le dialog d'ajout/modification
    const openDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                name: item.name || '',
                description: item.description || '',
                price: (item.price || '').toString(),
                category_id: item.category_id || activeCategory,
                image_url: item.image_url || '',
                sort_order: item.sort_order || 999,
                visible: item.visible !== false,
                is_available: item.is_available !== false,
                is_popular: item.is_popular || false,
                is_new: item.is_new || false
            });
        } else {
            setEditingItem(null);
            setFormData({
                name: '',
                description: '',
                price: '',
                category_id: activeCategory,
                image_url: '',
                sort_order: 999,
                visible: true,
                is_available: true,
                is_popular: false,
                is_new: false
            });
        }
        setIsDialogOpen(true);
    };
    
    // Upload image
    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.type.startsWith('image/')) {
            toast.error('Le fichier doit être une image');
            return;
        }
        
        if (file.size > 5 * 1024 * 1024) {
            toast.error('L\'image ne doit pas dépasser 5 Mo');
            return;
        }
        
        setUploading(true);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            const response = await api().post('/client-menu/admin/upload-image', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data?.url) {
                setFormData(prev => ({ ...prev, image_url: response.data.url }));
                toast.success('Image téléchargée');
            }
        } catch (error) {
            // Fallback base64
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, image_url: reader.result }));
                toast.success('Image ajoutée');
            };
            reader.readAsDataURL(file);
        } finally {
            setUploading(false);
        }
    };
    
    // ========================================
    // BULK IMPORT FUNCTIONS
    // ========================================
    
    // Handle CSV file upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (!file.name.endsWith('.csv')) {
            toast.error('Le fichier doit être au format CSV');
            return;
        }
        
        setBulkImporting(true);
        try {
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);
            
            const response = await api().post('/admin/client/menu/products/bulk-import-csv', formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            if (response.data?.success) {
                const { summary } = response.data;
                toast.success(`Import réussi: ${summary.created} créés, ${summary.updated} mis à jour, ${summary.skipped} ignorés`);
                fetchProducts();
                setShowBulkImport(false);
            }
        } catch (error) {
            console.error('CSV import error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'import CSV');
        } finally {
            setBulkImporting(false);
            if (csvInputRef.current) csvInputRef.current.value = '';
        }
    };
    
    // Handle JSON bulk import
    const handleBulkImport = async () => {
        if (!bulkImportData.trim()) {
            toast.error('Veuillez entrer des données à importer');
            return;
        }
        
        setBulkImporting(true);
        try {
            // Parse JSON data
            let products;
            try {
                products = JSON.parse(bulkImportData);
                if (!Array.isArray(products)) {
                    products = [products];
                }
            } catch {
                toast.error('Format JSON invalide');
                setBulkImporting(false);
                return;
            }
            
            // Map to expected format
            const mappedProducts = products.map(p => ({
                name: p.name || p.nom || '',
                category_name: p.category_name || p.categorie || p.category || activeCategory.replace('_', ' '),
                price: parseFloat(p.price || p.prix || 0),
                description: p.description || '',
                client_description: p.client_description || p.description || '',
                is_available: p.is_available !== false && p.disponible !== false,
                image_url: p.image_url || p.image || ''
            }));
            
            const response = await api().post('/admin/client/menu/products/bulk-import', {
                products: mappedProducts,
                update_existing: true,
                skip_duplicates: false
            });
            
            if (response.data?.success) {
                const { summary } = response.data;
                toast.success(`Import réussi: ${summary.created} créés, ${summary.updated} mis à jour`);
                
                if (response.data.errors?.length > 0) {
                    console.warn('Import errors:', response.data.errors);
                    toast.warning(`${response.data.errors.length} erreur(s) lors de l'import`);
                }
                
                fetchProducts();
                setShowBulkImport(false);
                setBulkImportData('');
            }
        } catch (error) {
            console.error('Bulk import error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
        } finally {
            setBulkImporting(false);
        }
    };
    
    // Sauvegarder le produit
    const handleSave = async () => {
        if (!formData.name?.trim()) {
            toast.error('Le nom du produit est obligatoire');
            return;
        }
        if (!formData.price || isNaN(parseFloat(formData.price))) {
            toast.error('Le prix est obligatoire');
            return;
        }
        
        setSaving(true);
        try {
            const payload = {
                name: formData.name.trim(),
                description: formData.description?.trim() || '',
                price: parseFloat(formData.price),
                category_id: formData.category_id,
                image_url: formData.image_url || null,
                sort_order: parseInt(formData.sort_order) || 999,
                visible: formData.visible,
                is_available: formData.is_available,
                is_popular: formData.is_popular,
                is_new: formData.is_new
            };
            
            if (editingItem) {
                await api().put(`/client-menu/admin/products/${editingItem.id}`, payload);
                toast.success('Produit modifié');
            } else {
                await api().post('/client-menu/admin/products', payload);
                toast.success('Produit ajouté au site client');
            }
            
            setIsDialogOpen(false);
            fetchProducts();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Save error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };
    
    // Supprimer un produit
    const handleDelete = async (item) => {
        if (!window.confirm(`Supprimer "${item.name}" du site client ?`)) return;
        
        try {
            await api().delete(`/client-menu/admin/products/${item.id}`);
            toast.success('Produit supprimé du site');
            fetchProducts();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };
    
    // Toggle visibilité
    const toggleVisibility = async (item) => {
        try {
            await api().put(`/client-menu/admin/products/${item.id}/visibility`, {
                visible: !item.visible
            });
            toast.success(item.visible ? 'Produit masqué' : 'Produit visible');
            fetchProducts();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    // Toggle disponibilité
    const toggleAvailability = async (item) => {
        try {
            await api().put(`/client-menu/admin/products/${item.id}/availability`, {
                is_available: !item.is_available
            });
            toast.success(item.is_available ? 'Produit indisponible' : 'Produit disponible');
            fetchProducts();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6">
            {/* Info banner - Catalogue séparé */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <Scale className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <h3 className="font-semibold text-blue-300">Catalogue Site Client - Indépendant</h3>
                        <p className="text-sm text-blue-200/70 mt-1">
                            Ce catalogue est <strong>séparé</strong> du système de caisse. Les produits ajoutés ici apparaissent <strong>uniquement</strong> sur le site internet public. 
                            Ils n'affectent pas la caisse, la cuisine, le bar, ni l'inventaire.
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Category tabs with counts */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                {MENU_CATEGORIES.map(cat => {
                    const Icon = cat.icon;
                    const isActive = activeCategory === cat.id;
                    const count = getCategoryCount(cat.id);
                    return (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                                isActive
                                    ? 'bg-green-600 text-white'
                                    : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                            }`}
                        >
                            <Icon className="w-4 h-4" />
                            {cat.name}
                            {count > 0 && (
                                <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                                    isActive ? 'bg-white/20' : 'bg-neutral-600'
                                }`}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            
            {/* Header avec boutons ajouter et import */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        {categoryInfo && <categoryInfo.icon className="w-5 h-5 text-amber-400" />}
                        {categoryInfo?.name || 'Catégorie'}
                    </h2>
                    <p className="text-sm text-neutral-400 mt-1">
                        {items.length} produit{items.length > 1 ? 's' : ''} dans cette catégorie
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowBulkImport(true)}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                        data-testid="bulk-import-btn"
                    >
                        <Upload className="w-5 h-5" />
                        Import en masse
                    </button>
                    <button
                        onClick={() => openDialog()}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                        data-testid="add-product-btn"
                    >
                        <Plus className="w-5 h-5" />
                        Ajouter un produit
                    </button>
                </div>
            </div>
            
            {/* BULK IMPORT MODAL */}
            <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
                <DialogContent className="bg-neutral-900 border-neutral-700 text-white max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                            <Upload className="w-5 h-5 text-blue-400" />
                            Import en masse de produits
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            Importez plusieurs produits à la fois via CSV ou JSON
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                        {/* CSV Import */}
                        <div className="bg-neutral-800 rounded-lg p-4 border border-neutral-700">
                            <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4 text-green-400" />
                                Import CSV
                            </h3>
                            <p className="text-sm text-neutral-400 mb-3">
                                Format attendu: <code className="bg-neutral-700 px-1 rounded">name,category_name,price,description,is_available</code>
                            </p>
                            <input
                                type="file"
                                accept=".csv"
                                ref={csvInputRef}
                                onChange={handleCsvUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => csvInputRef.current?.click()}
                                disabled={bulkImporting}
                                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 disabled:bg-green-800 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                            >
                                {bulkImporting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Import en cours...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Sélectionner un fichier CSV
                                    </>
                                )}
                            </button>
                        </div>
                        
                        {/* JSON Import */}
                        <div className="bg-neutral-800 rounded-lg p-4 border border-neutral-700">
                            <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-blue-400" />
                                Import JSON
                            </h3>
                            <p className="text-sm text-neutral-400 mb-3">
                                Collez une liste JSON de produits
                            </p>
                            <textarea
                                value={bulkImportData}
                                onChange={(e) => setBulkImportData(e.target.value)}
                                placeholder={`[\n  {"name": "Poulet braisé", "category_name": "Plats", "price": 5000, "description": "Délicieux poulet"},\n  {"name": "Tilapia grillé", "category_name": "Plats", "price": 6000}\n]`}
                                className="w-full h-40 bg-neutral-900 border border-neutral-600 rounded-lg p-3 text-sm text-white placeholder:text-neutral-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono"
                            />
                            <button
                                onClick={handleBulkImport}
                                disabled={bulkImporting || !bulkImportData.trim()}
                                className="w-full mt-3 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 disabled:opacity-50 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                            >
                                {bulkImporting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        Import en cours...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Importer les produits JSON
                                    </>
                                )}
                            </button>
                        </div>
                        
                        {/* Example template */}
                        <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
                            <h4 className="font-medium text-amber-400 mb-2">Exemple de template CSV</h4>
                            <pre className="text-xs text-neutral-300 overflow-x-auto">
{`name,category_name,price,description,is_available
"Poulet braisé","Plats principaux",5000,"Poulet fermier braisé",true
"Tilapia grillé","Plats principaux",6000,"Tilapia frais du lac",true
"Jus de bissap","Boissons fraîches",1500,"Jus de hibiscus maison",true`}
                            </pre>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Products grid */}
            {items.length === 0 ? (
                <div className="text-center py-12 text-neutral-400">
                    <UtensilsCrossed className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun produit dans cette catégorie</p>
                    <button
                        onClick={() => openDialog()}
                        className="mt-4 text-amber-400 hover:text-amber-300 underline"
                    >
                        Ajouter le premier produit
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map(item => (
                        <div
                            key={item.id}
                            className={`bg-neutral-800 rounded-xl border overflow-hidden transition-all ${
                                !item.visible 
                                    ? 'border-neutral-700 opacity-60' 
                                    : item.is_available 
                                        ? 'border-green-600/30' 
                                        : 'border-orange-600/30'
                            }`}
                        >
                            {/* Image */}
                            <div className="h-32 bg-neutral-900 relative">
                                {item.image_url ? (
                                    <img
                                        src={item.image_url.startsWith('data:') || item.image_url.startsWith('http') ? item.image_url : `${API_URL}${item.image_url}`}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <ImageIcon className="w-10 h-10 text-neutral-600" />
                                    </div>
                                )}
                                
                                {/* Status badges */}
                                <div className="absolute top-2 right-2 flex gap-1">
                                    {!item.visible && (
                                        <span className="bg-neutral-700 text-neutral-300 px-2 py-0.5 rounded text-xs">
                                            Masqué
                                        </span>
                                    )}
                                    {item.visible && !item.is_available && (
                                        <span className="bg-orange-600 text-white px-2 py-0.5 rounded text-xs">
                                            Indisponible
                                        </span>
                                    )}
                                    {item.is_new && (
                                        <span className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs">
                                            Nouveau
                                        </span>
                                    )}
                                    {item.is_popular && (
                                        <span className="bg-amber-500 text-black px-2 py-0.5 rounded text-xs">
                                            Populaire
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Content */}
                            <div className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                    <h3 className="font-semibold text-white truncate flex-1">{item.name}</h3>
                                    <span className="text-green-400 font-bold ml-2 whitespace-nowrap">
                                        {item.price?.toLocaleString()} F
                                    </span>
                                </div>
                                
                                {item.description && (
                                    <p className="text-sm text-neutral-400 line-clamp-2 mb-3">
                                        {item.description}
                                    </p>
                                )}
                                
                                {/* Status row */}
                                <div className="flex items-center gap-2 text-xs mb-3">
                                    <span className={`flex items-center gap-1 ${item.visible ? 'text-green-400' : 'text-neutral-500'}`}>
                                        {item.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                        {item.visible ? 'Visible' : 'Masqué'}
                                    </span>
                                    <span className="text-neutral-600">•</span>
                                    <span className={item.is_available ? 'text-green-400' : 'text-orange-400'}>
                                        {item.is_available ? 'Disponible' : 'Indisponible'}
                                    </span>
                                </div>
                                
                                {/* Actions */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openDialog(item)}
                                        className="flex-1 bg-neutral-700 hover:bg-neutral-600 text-white py-2 rounded-lg text-sm transition-colors flex items-center justify-center gap-1"
                                    >
                                        <Pencil className="w-4 h-4" />
                                        Modifier
                                    </button>
                                    <button
                                        onClick={() => toggleVisibility(item)}
                                        className={`p-2 rounded-lg transition-colors ${
                                            item.visible 
                                                ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' 
                                                : 'bg-neutral-700 text-neutral-400 hover:bg-neutral-600'
                                        }`}
                                        title={item.visible ? 'Masquer' : 'Afficher'}
                                    >
                                        {item.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => handleDelete(item)}
                                        className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-colors"
                                        title="Supprimer"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            {/* Dialog Ajout/Modification */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-neutral-900 border-neutral-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold flex items-center gap-2">
                            {editingItem ? (
                                <>
                                    <Pencil className="w-5 h-5 text-amber-400" />
                                    Modifier le produit
                                </>
                            ) : (
                                <>
                                    <Plus className="w-5 h-5 text-green-400" />
                                    Ajouter un produit au site
                                </>
                            )}
                        </DialogTitle>
                        <DialogDescription className="text-neutral-400">
                            Ce produit apparaîtra uniquement sur le site internet public.
                            Il n'affecte pas la caisse ni l'inventaire.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        {/* Nom */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Nom du produit <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white placeholder-neutral-500 focus:outline-none focus:border-amber-500"
                                placeholder="Ex: Poulet Braisé"
                                data-testid="product-name-input"
                            />
                        </div>
                        
                        {/* Catégorie */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Catégorie <span className="text-red-400">*</span>
                            </label>
                            <select
                                value={formData.category_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-amber-500"
                                data-testid="product-category-select"
                            >
                                {MENU_CATEGORIES.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                        </div>
                        
                        {/* Prix */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Prix affiché (FCFA) <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="number"
                                value={formData.price}
                                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white placeholder-neutral-500 focus:outline-none focus:border-amber-500"
                                placeholder="Ex: 3500"
                                min="0"
                                data-testid="product-price-input"
                            />
                        </div>
                        
                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Description courte
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white placeholder-neutral-500 focus:outline-none focus:border-amber-500 resize-none h-20"
                                placeholder="Description visible sur le site..."
                                data-testid="product-description-input"
                            />
                        </div>
                        
                        {/* Image */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Image
                            </label>
                            <div className="flex gap-3">
                                {formData.image_url && (
                                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-neutral-800 flex-shrink-0">
                                        <img
                                            src={formData.image_url.startsWith('data:') || formData.image_url.startsWith('http') ? formData.image_url : `${API_URL}${formData.image_url}`}
                                            alt="Preview"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleImageUpload}
                                        accept="image/*"
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="w-full bg-neutral-800 border border-dashed border-neutral-600 hover:border-amber-500 rounded-lg py-3 text-neutral-400 hover:text-amber-400 transition-colors flex items-center justify-center gap-2"
                                    >
                                        {uploading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Téléchargement...
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                {formData.image_url ? 'Changer l\'image' : 'Ajouter une image'}
                                            </>
                                        )}
                                    </button>
                                    {formData.image_url && (
                                        <button
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                                            className="mt-2 text-sm text-red-400 hover:text-red-300"
                                        >
                                            Supprimer l'image
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        {/* Ordre d'affichage */}
                        <div>
                            <label className="block text-sm font-medium text-neutral-300 mb-1">
                                Ordre d'affichage
                            </label>
                            <input
                                type="number"
                                value={formData.sort_order}
                                onChange={(e) => setFormData(prev => ({ ...prev, sort_order: e.target.value }))}
                                className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-2.5 text-white placeholder-neutral-500 focus:outline-none focus:border-amber-500"
                                placeholder="999"
                                min="1"
                            />
                            <p className="text-xs text-neutral-500 mt-1">Plus le nombre est petit, plus le produit apparaît en haut</p>
                        </div>
                        
                        {/* Toggles */}
                        <div className="grid grid-cols-2 gap-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.visible}
                                    onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                                    className="w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-green-500 focus:ring-green-500"
                                />
                                <span className="text-sm text-neutral-300">Visible sur le site</span>
                            </label>
                            
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_available}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_available: e.target.checked }))}
                                    className="w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-green-500 focus:ring-green-500"
                                />
                                <span className="text-sm text-neutral-300">Disponible</span>
                            </label>
                            
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_new}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_new: e.target.checked }))}
                                    className="w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-blue-500 focus:ring-blue-500"
                                />
                                <span className="text-sm text-neutral-300">Badge "Nouveau"</span>
                            </label>
                            
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.is_popular}
                                    onChange={(e) => setFormData(prev => ({ ...prev, is_popular: e.target.checked }))}
                                    className="w-5 h-5 rounded border-neutral-600 bg-neutral-800 text-amber-500 focus:ring-amber-500"
                                />
                                <span className="text-sm text-neutral-300">Badge "Populaire"</span>
                            </label>
                        </div>
                        
                        {/* Info box */}
                        <div className="bg-neutral-800 border border-neutral-700 rounded-lg p-3">
                            <p className="text-xs text-neutral-400">
                                <strong className="text-neutral-300">Note :</strong> Ce produit sera affiché uniquement sur le site internet public. 
                                Il n'apparaîtra pas dans la caisse, ne sera pas envoyé en cuisine/bar, et n'impactera pas l'inventaire.
                            </p>
                        </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-3 mt-6">
                        <button
                            type="button"
                            onClick={() => setIsDialogOpen(false)}
                            className="flex-1 bg-neutral-700 hover:bg-neutral-600 text-white py-2.5 rounded-lg font-medium transition-colors"
                        >
                            Annuler
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                            data-testid="save-product-btn"
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {editingItem ? 'Enregistrer' : 'Ajouter au site'}
                                </>
                            )}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// ============================================================
// ONGLET RÉSERVATIONS
// ============================================================
const ReservationsTab = () => {
    const { api } = useAuth();
    const [reservations, setReservations] = useState([]);
    const [clientSettings, setClientSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [saving, setSaving] = useState(false);
    
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [resResponse, settingsResponse] = await Promise.all([
                api().get('/admin/reservations'),
                api().get('/admin/client-settings')
            ]);
            setReservations(resResponse.data);
            setClientSettings(settingsResponse.data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => { fetchData(); }, [fetchData]);
    
    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await api().put('/admin/client-settings', clientSettings);
            toast.success('Paramètres enregistrés');
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    const updateStatus = async (id, status) => {
        try {
            await api().put(`/admin/reservations/${id}/status`, null, { params: { status } });
            toast.success('Statut mis à jour');
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };
    
    const filteredReservations = filter === 'all' ? reservations : reservations.filter(r => r.type === filter);
    
    const STATUS_COLORS = {
        pending: 'bg-yellow-500/20 text-yellow-400',
        confirmed: 'bg-green-500/20 text-green-400',
        cancelled: 'bg-red-500/20 text-red-400',
        completed: 'bg-blue-500/20 text-blue-400'
    };
    
    return (
        <div className="space-y-6">
            {/* Paramètres réservation */}
            <div className="bg-neutral-800/50 rounded-xl p-5 border border-neutral-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-amber-400" />
                    Paramètres de réservation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm text-neutral-400 mb-1">Capacité max table (personnes)</label>
                        <input
                            type="number"
                            value={clientSettings.table_max_capacity || 8}
                            onChange={(e) => setClientSettings({...clientSettings, table_max_capacity: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-neutral-400 mb-1">Capacité max événement (personnes)</label>
                        <input
                            type="number"
                            value={clientSettings.event_max_capacity || 30}
                            onChange={(e) => setClientSettings({...clientSettings, event_max_capacity: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:border-amber-500 focus:outline-none"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm text-neutral-400 mb-1">Message d'accueil - Réservation table</label>
                        <textarea
                            rows={2}
                            value={clientSettings.table_welcome_message || ''}
                            onChange={(e) => setClientSettings({...clientSettings, table_welcome_message: e.target.value})}
                            className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:border-amber-500 focus:outline-none resize-none"
                            placeholder="Message affiché aux clients pour la réservation de table..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm text-neutral-400 mb-1">Message d'accueil - Événement / Salle</label>
                        <textarea
                            rows={2}
                            value={clientSettings.event_welcome_message || ''}
                            onChange={(e) => setClientSettings({...clientSettings, event_welcome_message: e.target.value})}
                            className="w-full px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:border-amber-500 focus:outline-none resize-none"
                            placeholder="Message affiché aux clients pour les événements..."
                        />
                    </div>
                </div>
                <button onClick={handleSaveSettings} disabled={saving} className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
            </div>
            
            {/* Filtres */}
            <div className="flex gap-2">
                {[{ id: 'all', label: 'Toutes' }, { id: 'table', label: 'Tables' }, { id: 'event', label: 'Événements' }].map(f => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${filter === f.id ? 'bg-amber-500 text-black' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            
            {/* Liste des réservations */}
            {loading ? (
                <div className="text-center py-8 text-neutral-400">Chargement...</div>
            ) : filteredReservations.length === 0 ? (
                <div className="text-center py-12 text-neutral-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucune réservation</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredReservations.map(res => (
                        <div key={res.id} className="bg-neutral-800/50 rounded-xl p-4 border border-neutral-700">
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        {res.type === 'table' ? (
                                            <Calendar className="w-5 h-5 text-green-400" />
                                        ) : (
                                            <PartyPopper className="w-5 h-5 text-amber-400" />
                                        )}
                                        <span className="font-semibold text-white">{res.name}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[res.status] || 'bg-neutral-600 text-neutral-300'}`}>
                                            {res.status === 'pending' ? 'En attente' : res.status === 'confirmed' ? 'Confirmée' : res.status === 'cancelled' ? 'Annulée' : 'Terminée'}
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-neutral-400">
                                        <span><Calendar className="w-3 h-3 inline mr-1" />{res.date}</span>
                                        {res.time && <span><Clock className="w-3 h-3 inline mr-1" />{res.time}</span>}
                                        <span><Users className="w-3 h-3 inline mr-1" />{res.guests} pers.</span>
                                        <span><Phone className="w-3 h-3 inline mr-1" />{res.phone}</span>
                                    </div>
                                    {res.event_type && <p className="text-sm text-amber-400 mt-1">Type: {res.event_type}</p>}
                                    {(res.notes || res.message) && <p className="text-sm text-neutral-500 mt-1 italic">"{res.notes || res.message}"</p>}
                                </div>
                                <div className="flex gap-2 ml-4">
                                    {res.status === 'pending' && (
                                        <>
                                            <button onClick={() => updateStatus(res.id, 'confirmed')} className="p-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg">
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => updateStatus(res.id, 'cancelled')} className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============================================================
// ONGLET PARAMÈTRES
// ============================================================
// ============================================================
// ONGLET PARAMÈTRES - REFONTE COMPLÈTE
// Design moderne, ergonomique, connecté au site client
// ============================================================
const SettingsTab = ({ settings, setSettings, onSave, saving }) => {
    const [hasChanges, setHasChanges] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const originalSettings = useRef(JSON.stringify(settings));
    
    // Detect changes
    useEffect(() => {
        const currentSettings = JSON.stringify(settings);
        setHasChanges(currentSettings !== originalSettings.current);
    }, [settings]);
    
    // Update field helper
    const updateField = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
        setSaveSuccess(false);
        setSaveError(null);
    };
    
    // Update nested field (for social links)
    const updateSocial = (field, value) => {
        setSettings(prev => ({
            ...prev,
            social: { ...(prev.social || {}), [field]: value }
        }));
        setSaveSuccess(false);
        setSaveError(null);
    };
    
    // Update display options
    const updateDisplayOption = (field, value) => {
        setSettings(prev => ({
            ...prev,
            display_options: { ...(prev.display_options || {}), [field]: value }
        }));
        setSaveSuccess(false);
        setSaveError(null);
    };
    
    // Handle save
    const handleSave = async (publish = false) => {
        setSaveError(null);
        try {
            await onSave();
            setSaveSuccess(true);
            setLastSaved(new Date());
            originalSettings.current = JSON.stringify(settings);
            setHasChanges(false);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            setSaveError("Erreur lors de l'enregistrement. Veuillez réessayer.");
        }
    };
    
    // Format last saved time
    const formatLastSaved = () => {
        if (!lastSaved) return null;
        const diff = Math.floor((new Date() - lastSaved) / 1000);
        if (diff < 60) return "à l'instant";
        if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
        return lastSaved.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };
    
    // Toggle component
    const Toggle = ({ checked, onChange, label, description }) => (
        <div className="flex items-center justify-between py-3 border-b border-neutral-700/50 last:border-0">
            <div>
                <span className="text-white font-medium">{label}</span>
                {description && <p className="text-xs text-neutral-500 mt-0.5">{description}</p>}
            </div>
            <button
                onClick={() => onChange(!checked)}
                className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    checked ? 'bg-green-500' : 'bg-neutral-600'
                }`}
            >
                <span 
                    className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        checked ? 'translate-x-6' : 'translate-x-0'
                    }`}
                />
            </button>
        </div>
    );
    
    // Input field component
    const InputField = ({ icon: Icon, label, value, onChange, type = 'text', placeholder }) => (
        <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm font-medium text-neutral-400">
                {Icon && <Icon className="w-4 h-4" />}
                {label}
            </label>
            <input
                type={type}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-2.5 bg-neutral-900/80 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 focus:outline-none transition-all"
            />
        </div>
    );
    
    // Section card component
    const SectionCard = ({ icon: Icon, iconColor, title, children }) => (
        <div className="bg-neutral-800/60 backdrop-blur-sm rounded-2xl border border-neutral-700/50 overflow-hidden">
            <div className="px-5 py-4 border-b border-neutral-700/50 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconColor}`}>
                    <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">{title}</h3>
            </div>
            <div className="p-5">
                {children}
            </div>
        </div>
    );
    
    return (
        <div className="space-y-6 pb-24">
            {/* Status Banner */}
            <div className={`rounded-xl p-4 flex items-center justify-between ${
                hasChanges 
                    ? 'bg-amber-500/10 border border-amber-500/30' 
                    : 'bg-green-500/10 border border-green-500/30'
            }`}>
                <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${hasChanges ? 'bg-amber-500 animate-pulse' : 'bg-green-500'}`} />
                    <div>
                        <span className={`font-medium ${hasChanges ? 'text-amber-400' : 'text-green-400'}`}>
                            {hasChanges ? 'Modifications non enregistrées' : 'Site client synchronisé'}
                        </span>
                        {lastSaved && !hasChanges && (
                            <span className="text-neutral-500 text-sm ml-2">
                                • Dernière mise à jour : {formatLastSaved()}
                            </span>
                        )}
                    </div>
                </div>
                <a 
                    href="/client" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg text-white text-sm font-medium transition-colors"
                >
                    <ExternalLink className="w-4 h-4" />
                    Voir le site
                </a>
            </div>
            
            {/* Success/Error Messages */}
            {saveSuccess && (
                <div className="bg-green-500/20 border border-green-500/40 rounded-xl p-4 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-300 font-medium">Paramètres enregistrés avec succès</span>
                </div>
            )}
            {saveError && (
                <div className="bg-red-500/20 border border-red-500/40 rounded-xl p-4 flex items-center gap-3">
                    <X className="w-5 h-5 text-red-400" />
                    <span className="text-red-300 font-medium">{saveError}</span>
                </div>
            )}
            
            {/* Grid Layout for Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* 1. Informations du restaurant */}
                <SectionCard icon={Phone} iconColor="bg-blue-500" title="Informations du restaurant">
                    <div className="space-y-4">
                        <InputField
                            icon={Phone}
                            label="Téléphone"
                            value={settings.phone}
                            onChange={(v) => updateField('phone', v)}
                            type="tel"
                            placeholder="+241 77 71 27 78"
                        />
                        <InputField
                            icon={Mail}
                            label="Email"
                            value={settings.email}
                            onChange={(v) => updateField('email', v)}
                            type="email"
                            placeholder="aschrislay@gmail.com"
                        />
                        <InputField
                            icon={MapPin}
                            label="Adresse"
                            value={settings.address}
                            onChange={(v) => updateField('address', v)}
                            placeholder="Damas, Libreville..."
                        />
                        <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-sm font-medium text-neutral-400">
                                <Clock className="w-4 h-4" />
                                Horaires d'ouverture
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    value={settings.hours_weekday || 'Lun-Sam: 08h-19h'}
                                    onChange={(e) => updateField('hours_weekday', e.target.value)}
                                    className="px-4 py-2.5 bg-neutral-900/80 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none text-sm"
                                    placeholder="Lun-Sam: 08h-19h"
                                />
                                <input
                                    type="text"
                                    value={settings.hours_sunday || 'Dim: Fermé'}
                                    onChange={(e) => updateField('hours_sunday', e.target.value)}
                                    className="px-4 py-2.5 bg-neutral-900/80 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none text-sm"
                                    placeholder="Dim: Fermé"
                                />
                            </div>
                        </div>
                    </div>
                </SectionCard>
                
                {/* 2. Réseaux sociaux */}
                <SectionCard icon={Instagram} iconColor="bg-gradient-to-br from-purple-500 to-pink-500" title="Réseaux sociaux">
                    <div className="space-y-4">
                        <InputField
                            icon={Instagram}
                            label="Instagram"
                            value={settings.social?.instagram || settings.instagram}
                            onChange={(v) => updateSocial('instagram', v)}
                            placeholder="https://instagram.com/aldrins"
                        />
                        <InputField
                            icon={Facebook}
                            label="Facebook"
                            value={settings.social?.facebook || settings.facebook}
                            onChange={(v) => updateSocial('facebook', v)}
                            placeholder="https://facebook.com/aldrins"
                        />
                        <div className="space-y-1.5">
                            <label className="flex items-center gap-2 text-sm font-medium text-neutral-400">
                                <Phone className="w-4 h-4" />
                                WhatsApp
                            </label>
                            <input
                                type="text"
                                value={settings.whatsapp || settings.phone || ''}
                                onChange={(e) => updateField('whatsapp', e.target.value)}
                                placeholder="+241 77 71 27 78"
                                className="w-full px-4 py-2.5 bg-neutral-900/80 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none"
                            />
                        </div>
                        <InputField
                            icon={MapPin}
                            label="Lien Google Maps"
                            value={settings.google_maps_url}
                            onChange={(v) => updateField('google_maps_url', v)}
                            placeholder="https://maps.google.com/..."
                        />
                    </div>
                </SectionCard>
                
                {/* 3. Affichage du site client */}
                <SectionCard icon={Eye} iconColor="bg-emerald-500" title="Affichage du site client">
                    <div className="space-y-1">
                        <Toggle
                            checked={settings.show_prices !== false}
                            onChange={(v) => updateField('show_prices', v)}
                            label="Afficher les prix"
                            description="Les prix seront visibles sur la carte en ligne"
                        />
                        <Toggle
                            checked={settings.show_descriptions !== false}
                            onChange={(v) => updateField('show_descriptions', v)}
                            label="Afficher les descriptions"
                            description="Descriptions détaillées des plats"
                        />
                        <Toggle
                            checked={settings.show_empty_categories !== true}
                            onChange={(v) => updateField('show_empty_categories', !v)}
                            label="Masquer catégories vides"
                            description="Ne pas afficher les catégories sans produits"
                        />
                        <Toggle
                            checked={settings.reservations_enabled !== false}
                            onChange={(v) => updateField('reservations_enabled', v)}
                            label="Activer les réservations"
                            description="Permettre aux clients de réserver en ligne"
                        />
                    </div>
                </SectionCard>
                
                {/* 4. Publication & synchronisation */}
                <SectionCard icon={Settings} iconColor="bg-amber-500" title="Publication & synchronisation">
                    <div className="space-y-4">
                        {/* Status */}
                        <div className="flex items-center justify-between p-3 bg-neutral-900/50 rounded-xl">
                            <div className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded-full bg-green-500" />
                                <span className="text-neutral-300">Statut du site</span>
                            </div>
                            <span className="text-green-400 font-medium">Connecté</span>
                        </div>
                        
                        {/* Last update */}
                        <div className="flex items-center justify-between p-3 bg-neutral-900/50 rounded-xl">
                            <span className="text-neutral-300">Dernière mise à jour</span>
                            <span className="text-neutral-400 text-sm">
                                {lastSaved ? formatLastSaved() : 'Non disponible'}
                            </span>
                        </div>
                        
                        {/* Site URL */}
                        <div className="p-3 bg-neutral-900/50 rounded-xl">
                            <label className="text-sm text-neutral-400 mb-2 block">URL du site client</label>
                            <div className="flex items-center gap-2">
                                <code className="flex-1 px-3 py-2 bg-neutral-800 rounded-lg text-amber-400 text-sm truncate">
                                    {window.location.origin}/client
                                </code>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(`${window.location.origin}/client`);
                                        toast.success('URL copiée !');
                                    }}
                                    className="p-2 bg-neutral-700 hover:bg-neutral-600 rounded-lg transition-colors"
                                >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        
                        {/* View site button */}
                        <a 
                            href="/client" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 py-3 bg-neutral-700 hover:bg-neutral-600 rounded-xl text-white font-medium transition-colors"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Voir le site client
                        </a>
                    </div>
                </SectionCard>
            </div>
            
            {/* Sticky Save Button */}
            <div className="fixed bottom-0 left-0 right-0 lg:left-64 p-4 bg-neutral-900/95 backdrop-blur-sm border-t border-neutral-700 z-40">
                <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        {hasChanges && (
                            <>
                                <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                <span className="text-amber-400 text-sm font-medium hidden sm:inline">
                                    Modifications non enregistrées
                                </span>
                            </>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => handleSave(true)}
                            disabled={saving || !hasChanges}
                            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                                hasChanges && !saving
                                    ? 'bg-amber-500 hover:bg-amber-400 text-black shadow-lg shadow-amber-500/20'
                                    : 'bg-neutral-700 text-neutral-400 cursor-not-allowed'
                            }`}
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    <span>Enregistrement...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span className="hidden sm:inline">Enregistrer et publier</span>
                                    <span className="sm:hidden">Enregistrer</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// ONGLET DESCRIPTIONS
// ============================================================
const DescriptionsTab = ({ products, onRefresh }) => {
    const { api } = useAuth();
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    const filteredProducts = products.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const handleSave = async (productId) => {
        setSaving(true);
        try {
            await api().put(`/client/products/${productId}`, {
                client_description: editValue
            });
            toast.success('Description enregistrée');
            setEditingId(null);
            onRefresh();
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    return (
        <div className="space-y-4">
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className="w-full pl-10 pr-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none"
                />
            </div>
            
            {/* Products list */}
            <div className="space-y-2">
                {filteredProducts.map(product => (
                    <div key={product.id} className="bg-neutral-800 rounded-lg p-4 border border-neutral-600 hover:border-neutral-500 transition-colors">
                        <div className="flex justify-between items-start gap-4">
                            <div className="flex-1">
                                <h4 className="font-semibold text-white text-lg">{product.name}</h4>
                                {editingId === product.id ? (
                                    <div className="mt-2 space-y-2">
                                        <textarea
                                            rows={3}
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            className="w-full px-3 py-2 bg-neutral-900 border border-neutral-600 rounded-lg text-white focus:border-amber-500 focus:outline-none resize-none"
                                            placeholder="Description pour le site client..."
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleSave(product.id)}
                                                disabled={saving}
                                                className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium rounded-lg"
                                            >
                                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                                            </button>
                                            <button
                                                onClick={() => setEditingId(null)}
                                                className="px-3 py-1.5 bg-neutral-600 hover:bg-neutral-500 text-white text-sm rounded-lg"
                                            >
                                                Annuler
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-neutral-300 mt-1.5">
                                        {product.client_description || <span className="italic text-neutral-500">Aucune description</span>}
                                    </p>
                                )}
                            </div>
                            {editingId !== product.id && (
                                <button
                                    onClick={() => {
                                        setEditingId(product.id);
                                        setEditValue(product.client_description || '');
                                    }}
                                    className="p-2.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg border border-amber-500/30"
                                    title="Modifier la description"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ============================================================
// ONGLET DISPONIBILITÉ
// ============================================================
const DisponibiliteTab = ({ products, onRefresh }) => {
    const { api } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [updating, setUpdating] = useState(null);
    
    const filteredProducts = products.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const toggleVisibility = async (productId, currentVisible) => {
        setUpdating(productId);
        try {
            await api().put(`/admin/client/products/${productId}/availability`, {
                visible_client: !currentVisible
            });
            toast.success(currentVisible ? 'Masqué du site' : 'Visible sur le site');
            onRefresh();
        } catch (error) {
            console.error('Error toggling visibility:', error);
            toast.error('Erreur lors de la mise à jour');
        } finally {
            setUpdating(null);
        }
    };
    
    return (
        <div className="space-y-4">
            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un produit..."
                    className="w-full pl-10 pr-4 py-2.5 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder-neutral-500 focus:border-amber-500 focus:outline-none"
                />
            </div>
            
            {/* Products grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredProducts.map(product => {
                    // Check both field names for backward compatibility
                    const isVisible = product.visible_client !== false && product.visible_on_client !== false;
                    return (
                        <div 
                            key={product.id} 
                            className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                                isVisible 
                                    ? 'bg-green-900/20 border-green-700/50' 
                                    : 'bg-neutral-800/50 border-neutral-700'
                            }`}
                        >
                            <div className="flex-1 min-w-0">
                                <h4 className={`font-medium truncate ${isVisible ? 'text-white' : 'text-neutral-400'}`}>
                                    {product.name}
                                </h4>
                                <p className="text-xs text-neutral-500">
                                    {product.category_name || product.category || '-'}
                                </p>
                            </div>
                            <button
                                onClick={() => toggleVisibility(product.id, isVisible)}
                                disabled={updating === product.id}
                                className={`p-2 rounded-lg transition-all ${
                                    isVisible
                                        ? 'bg-green-600 hover:bg-green-500 text-white'
                                        : 'bg-neutral-700 hover:bg-neutral-600 text-neutral-400'
                                }`}
                            >
                                {updating === product.id ? (
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                ) : isVisible ? (
                                    <Eye className="w-4 h-4" />
                                ) : (
                                    <EyeOff className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================
// ONGLET PAGES DU SITE - À propos et CGV
// ============================================================
const PagesTab = () => {
    const { api } = useAuth();
    const [pages, setPages] = useState({
        about: '',
        terms: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);
    const [activePageTab, setActivePageTab] = useState('about');
    
    // Default content
    const defaultAbout = `Chez Aldrin's Restaurant, nous faisons le choix de l'authenticité et de la maîtrise.

Nous travaillons en priorité avec des produits issus des côtes gabonaises et valorisons autant que possible les ressources locales. Cette approche n'est pas seulement un engagement, c'est une manière de soutenir notre environnement, notre économie et de garantir la fraîcheur de nos plats.

Notre réalité quotidienne est simple : produire mieux, avec rigueur.
La maîtrise des coûts de production fait partie intégrante de notre fonctionnement, afin de proposer une cuisine accessible sans compromis sur la qualité.

Notre équipe est composée majoritairement de professionnels gabonais, engagés, formés et responsables. Nous croyons en la compétence locale et en la capacité de chacun à évoluer dans un cadre structuré et exigeant.

Chez Aldrin's, le sens du détail est une priorité.
Chaque élément compte : la préparation, la présentation, l'organisation, le service.

Nous nous attachons à rester compétitifs, tout en maintenant un niveau d'exigence élevé.
L'hygiène n'est pas une option, c'est une base.
L'accueil, le sourire et le respect du client font partie de notre identité.

Notre ambition est claire : offrir une expérience simple, propre, maîtrisée et constante.

Aldrin's Restaurant, c'est une cuisine locale assumée, un service structuré, et une exigence quotidienne.`;

    const defaultTerms = `Les présentes conditions générales de vente et d'utilisation (ci-après les « Conditions ») ont pour objet de définir les modalités d'accès, de fréquentation et de consommation au sein de l'établissement Aldrin's Restaurant.

Toute personne accédant à l'établissement, procédant à une réservation ou consommant un service reconnaît avoir pris connaissance des présentes Conditions et les accepter sans réserve.

---

1. Identification de l'établissement
Aldrin's Restaurant est un établissement de restauration offrant des prestations de vente de produits alimentaires et de services associés, dans un cadre professionnel structuré.

---

2. Champ d'application
Les présentes Conditions s'appliquent à l'ensemble des prestations proposées par Aldrin's Restaurant, sans exception.
Elles prévalent sur tout autre document ou communication.

(... suite des CGV ...)`;
    
    // Fetch pages content
    useEffect(() => {
        const fetchPages = async () => {
            try {
                const response = await api.get(`${API_URL}/api/admin/pages`);
                setPages({
                    about: response.data.about || defaultAbout,
                    terms: response.data.terms || defaultTerms
                });
            } catch (error) {
                console.log('Using default pages content');
                setPages({
                    about: defaultAbout,
                    terms: defaultTerms
                });
            } finally {
                setLoading(false);
            }
        };
        fetchPages();
    }, [api]);
    
    // Save page content
    const savePage = async (pageName) => {
        setSaving(pageName);
        try {
            await api.put(`${API_URL}/api/admin/pages/${pageName}`, {
                content: pages[pageName]
            });
            toast.success(pageName === 'about' ? 'Page "À propos" enregistrée' : 'CGV enregistrées');
        } catch (error) {
            console.error('Error saving page:', error);
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(null);
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    return (
        <div className="space-y-6">
            {/* Info card */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                        <h4 className="text-blue-400 font-medium">Pages du site client</h4>
                        <p className="text-sm text-blue-300/70 mt-1">
                            Modifiez le contenu des pages "À propos" et "Conditions générales" directement ici. 
                            Les modifications seront visibles immédiatement sur le site client.
                        </p>
                    </div>
                </div>
            </div>
            
            {/* Page tabs */}
            <div className="flex gap-2 border-b border-neutral-700 pb-2">
                <button
                    onClick={() => setActivePageTab('about')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all ${
                        activePageTab === 'about'
                            ? 'bg-neutral-700 text-white'
                            : 'text-neutral-400 hover:text-white'
                    }`}
                >
                    <BookOpen className="w-4 h-4" />
                    À propos de nous
                </button>
                <button
                    onClick={() => setActivePageTab('terms')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all ${
                        activePageTab === 'terms'
                            ? 'bg-neutral-700 text-white'
                            : 'text-neutral-400 hover:text-white'
                    }`}
                >
                    <Scale className="w-4 h-4" />
                    Conditions générales
                </button>
            </div>
            
            {/* About page */}
            {activePageTab === 'about' && (
                <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-amber-500" />
                            Page "À propos de nous"
                        </h3>
                        <a 
                            href="/client/about" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Voir la page
                        </a>
                    </div>
                    
                    <textarea
                        value={pages.about}
                        onChange={(e) => setPages(prev => ({ ...prev, about: e.target.value }))}
                        className="w-full h-96 bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-white resize-none focus:outline-none focus:border-amber-500"
                        placeholder="Contenu de la page À propos..."
                    />
                    
                    <div className="flex justify-end">
                        <button
                            onClick={() => savePage('about')}
                            disabled={saving === 'about'}
                            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            {saving === 'about' ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Enregistrer
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
            
            {/* Terms page */}
            {activePageTab === 'terms' && (
                <div className="bg-neutral-800 rounded-xl p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                            <Scale className="w-5 h-5 text-amber-500" />
                            Conditions générales de vente
                        </h3>
                        <a 
                            href="/client/terms" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-amber-500 hover:text-amber-400"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Voir la page
                        </a>
                    </div>
                    
                    <div className="text-sm text-neutral-400 mb-2">
                        Utilisez "---" pour séparer les sections. Utilisez "•" pour les listes à puces.
                    </div>
                    
                    <textarea
                        value={pages.terms}
                        onChange={(e) => setPages(prev => ({ ...prev, terms: e.target.value }))}
                        className="w-full h-96 bg-neutral-900 border border-neutral-700 rounded-lg p-4 text-white resize-none focus:outline-none focus:border-amber-500 font-mono text-sm"
                        placeholder="Contenu des conditions générales..."
                    />
                    
                    <div className="flex justify-end">
                        <button
                            onClick={() => savePage('terms')}
                            disabled={saving === 'terms'}
                            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black px-6 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            {saving === 'terms' ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Enregistrer
                                </>
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const ClientAdminPage = () => {
    const { api } = useAuth();
    const [activeTab, setActiveTab] = useState('carte');
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const settingsRes = await api().get('/admin/client-settings').catch(() => ({ data: {} }));
            setSettings(settingsRes.data || {});
        } catch (error) {
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    useEffect(() => { fetchData(); }, [fetchData]);
    
    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            await api().put('/admin/client-settings', settings);
            toast.success('Paramètres enregistrés');
        } catch (error) {
            toast.error('Erreur lors de l\'enregistrement');
        } finally {
            setSaving(false);
        }
    };
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-neutral-400">Chargement...</div>
            </div>
        );
    }
    
    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                    <Settings className="w-7 h-7 text-amber-400" />
                    Gestion du Site Client
                </h1>
                <p className="text-neutral-400 mt-1">
                    Catalogue indépendant du site internet • Séparé de la caisse et du stock
                </p>
            </div>
            
            {/* Tabs - 4 onglets principaux */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                <TabButton
                    active={activeTab === 'carte'}
                    onClick={() => setActiveTab('carte')}
                    icon={UtensilsCrossed}
                    label="Carte du Site"
                />
                <TabButton
                    active={activeTab === 'reservations'}
                    onClick={() => setActiveTab('reservations')}
                    icon={Calendar}
                    label="Réservations"
                />
                <TabButton
                    active={activeTab === 'parametres'}
                    onClick={() => setActiveTab('parametres')}
                    icon={Settings}
                    label="Paramètres"
                />
                <TabButton
                    active={activeTab === 'pages'}
                    onClick={() => setActiveTab('pages')}
                    icon={BookOpen}
                    label="Pages"
                />
            </div>
            
            {/* Content */}
            {activeTab === 'carte' && (
                <CarteTab onRefresh={fetchData} />
            )}
            
            {activeTab === 'reservations' && (
                <ReservationsTab />
            )}
            
            {activeTab === 'parametres' && (
                <SettingsTab
                    settings={settings}
                    setSettings={setSettings}
                    onSave={handleSaveSettings}
                    saving={saving}
                />
            )}
            
            {activeTab === 'pages' && (
                <PagesTab />
            )}
        </div>
    );
};

export default ClientAdminPage;
